import { useState } from 'react';
import { Activity, RefreshCw } from 'react-feather';
import { useLocation } from 'react-router-dom';
import Navigation from '../../components/Nav/Nav';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import { useGeneralContext } from '../../context';
import { categoryKeys } from './types';
import ExtractedNuggets from './ExtractedNuggets';
import TreeMap from './TreeMap';
import AboutExploreModal from '../exploration/AboutChatModal';
import { ChatWithJournalCard } from '../home/GptPromptCard';
import { EntryBlockWithContext } from '../../components/calendar/types';
import { chatWithJournal } from '../../api';
import { userIsFree } from '../../util/subscription';
import config from '../../config';

const Retrospective = () => {
  const context = useGeneralContext();
  const { nuggets, refreshNuggets, subscriptionStatus } = context;
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [loading] = useState(false);
  const [refreshBtnRotating, setRefreshBtnRotating] = useState(false);

  // Home's suggested questions land here, already asked.
  const location = useLocation<{ initialQuery?: string } | undefined>();
  const initialQuery = location.state?.initialQuery;
  const [showAboutChatModal, setShowAboutChatModal] = useState(false);
  const [showExploreBlockedModal, setShowExploreBlockedModal] = useState(false);

  const initConversation: EntryBlockWithContext[] = [
    { role: 'assistant', content: 'Hi there, I am a reflection of your entire journal', context: [] }
  ];

  const savedConversation = sessionStorage.getItem('conversation');
  let savedConvoParsed: EntryBlockWithContext[] | null = null;
  if (savedConversation !== null) {
    try {
      savedConvoParsed = JSON.parse(window.atob(savedConversation));
    } catch (err) {}
  }
  const [conversation, setConversation] = useState<EntryBlockWithContext[]>(savedConvoParsed || initConversation);

  const handleUserQuery = async (convo: EntryBlockWithContext[]) => {
    if (userIsFree(subscriptionStatus)) {
      setShowExploreBlockedModal(true);
      return;
    }
    // query journal with conversation and journal context
    const response = await chatWithJournal({ conversation: convo.slice(initConversation.length) });
    sessionStorage.setItem(
      'conversation',
      window.btoa(JSON.stringify([...initConversation, ...response.data.conversation]))
    );
    setConversation([...initConversation, ...response.data.conversation]);
  };

  const handleRefreshClick = () => {
    setRefreshBtnRotating(true);
    setTimeout(() => setRefreshBtnRotating(false), 1000);
    refreshNuggets();
  };

  return (
    <div className="tw-col-span-full tw-grid tw-h-full tw-grid-cols-[minmax(0,1fr)] lg:tw-grid-cols-[17rem_minmax(0,1fr)] tw-bg-[url('../../public/bg.png')] tw-bg-cover tw-bg-no-repeat tw-bg-center">
      <Navigation />
      <PremiumUpsellModal
        showModal={showPremiumModal}
        setShowModal={setShowPremiumModal}
        message={`Get access to your extracted ${categoryKeys
          .slice(1, categoryKeys.length - 1)
          .map((v) => `${v}s`)
          .join(', ')}, and ${categoryKeys[categoryKeys.length - 1]}s with a premium subscription`}
      />
      <PremiumUpsellModal
        showModal={showExploreBlockedModal}
        setShowModal={setShowExploreBlockedModal}
        message={`Upgrade to Premium (${config.freeTrialPeriodInDays} day free trial included) to unlock unlimited conversation with your own journal.`}
      />
      <AboutExploreModal showModal={showAboutChatModal} setShowModal={setShowAboutChatModal} />
      <div className="tw-flex tw-flex-col tw-gap-10 tw-px-4 tw-pt-20 tw-pb-10 sm:tw-px-6 lg:tw-col-start-2 lg:tw-m-8 lg:tw-p-10 lg:tw-pt-10 lg:tw-gap-12 lg:tw-bg-white lg:tw-rounded-3xl lg:tw-self-stretch tw-min-w-0">
        {/* --------------------------------------------------- header */}
        <section className="tw-flex tw-flex-col tw-gap-5 tw-min-w-0">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
            <h1 className="tw-m-0 tw-font-light tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-text-neutral-800">
              Explore your Journal
            </h1>
            {/* The refresh control was desktop-only and unlabelled; it is now
                reachable on a phone and meets the 44px tap target. */}
            <button
              onClick={handleRefreshClick}
              aria-label="Refresh"
              title="Refresh"
              className={`tw-flex tw-items-center tw-justify-center tw-min-h-touch-min tw-min-w-touch-min tw-rounded-lg tw-bg-white/70 tw-border tw-border-neutral-200 tw-cursor-pointer ${
                refreshBtnRotating ? 'tw-transform tw-transition-transform tw-duration-1000 tw-ease-in-out' : ''
              }`}
              style={refreshBtnRotating ? { transform: 'rotate(360deg)' } : {}}
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </section>

        {/* ------------------------------------------ chat with journal */}
        <section className="tw-flex tw-flex-col tw-gap-5 tw-min-w-0">
          <h2 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-font-light tw-text-xl lg:tw-text-2xl tw-text-neutral-800">
            <Activity className="tw-w-6 tw-h-6 tw-shrink-0" />
            Chat with your Journal
          </h2>
          <div className="tw-flex tw-h-[32rem]">
            <ChatWithJournalCard
              conversation={conversation}
              handleUserQuery={handleUserQuery}
              clearConversation={() => {
                sessionStorage.removeItem('conversation');
                setConversation(initConversation);
              }}
              placeholder="Ask me anything ..."
              autoSubmitQuery={initialQuery}
            />
          </div>
        </section>

        {/* ------------------------------------------- extracted nuggets */}
        <ExtractedNuggets nuggets={nuggets} loading={loading} setShowPremiumModal={setShowPremiumModal} />

        {/* ------------------------------------------------- journal map */}
        <TreeMap nuggets={nuggets} loading={loading} setShowPremiumModal={setShowPremiumModal} />
      </div>
    </div>
  );
};

export default Retrospective;
